import {
  createContext,
  PropsWithChildren,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import { useForm, UseFormReturn } from '../../../hooks/useForm'
import { OnboardingStep } from '../enums/onboardingStep'
import { OnboardingFormValidationSchema } from '../types/onboardingFormValidationSchema'
import { OnboardingStepRegistration } from '../types/onboardingStepRegistration'
import { getDefaultValues } from '../utils/getDefaultValues'
import { getOnboardingStepSchema } from '../utils/getOnboardingStepSchema'

interface OnboardingFormContextValue {
  isLoading: boolean
  isSubmitting: boolean
  onboardingForm: UseFormReturn<OnboardingFormValidationSchema>
  registerStep: (registration: OnboardingStepRegistration) => void
  submitStep: () => Promise<void>
  validateStep: () => Promise<boolean>
}

const defaultRegistration: OnboardingStepRegistration = {
  isLoading: false,
  isSubmitting: false,
  predefinedValues: undefined,
  submitHandler: () => Promise.resolve(),
}

const OnboardingFormContext = createContext<OnboardingFormContextValue | undefined>(undefined)

interface OnboardingFormContextProviderProps extends PropsWithChildren {
  step: OnboardingStep
}

export const OnboardingFormContextProvider = ({ children, step }: OnboardingFormContextProviderProps): ReactElement => {
  const [registration, setRegistration] = useState<OnboardingStepRegistration>(defaultRegistration)
  const prevStepRef = useRef(step)
  const { t } = useTranslation()

  const onboardingForm = useForm<OnboardingFormValidationSchema>({
    defaultValues: getDefaultValues(),
    validationSchema: getOnboardingStepSchema(step, t),
  })

  const { Form, getValues, handleSubmit, reset: resetForm, trigger: validateStep } = onboardingForm
  const { isLoading, isSubmitting, predefinedValues, submitHandler } = registration
  const submitStep = useCallback(() => handleSubmit(submitHandler)(), [handleSubmit, submitHandler])

  const registerStep = useCallback((registration: OnboardingStepRegistration) => {
    setRegistration(registration)
  }, [])

  useEffect(() => {
    if (predefinedValues && !isLoading) {
      const intents = getValues('intents')
      resetForm(getDefaultValues({ intents, ...predefinedValues }))
    }
  }, [getValues, isLoading, predefinedValues, resetForm])

  // Reset registration if step changes to avoid showing stale data/loading states
  if (prevStepRef.current !== step) {
    prevStepRef.current = step
    setRegistration(defaultRegistration)
  }

  const contextValue = useMemo<OnboardingFormContextValue>(
    () => ({
      isLoading,
      isSubmitting,
      onboardingForm,
      registerStep,
      submitStep,
      validateStep,
    }),
    [isLoading, isSubmitting, onboardingForm, registerStep, submitStep, validateStep],
  )

  return (
    <OnboardingFormContext.Provider value={contextValue}>
      <Form>{children}</Form>
    </OnboardingFormContext.Provider>
  )
}

export const useOnboardingFormContext = () => {
  const context = useContext(OnboardingFormContext)

  if (!context) {
    throw new Error('OnboardingFormContextProvider is missing in the module!')
  }

  return context
}
